import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUpdateResource } from '../../../hooks/useUpdateResource';
import InputField from '../../form/InputField';
import LoadingButton from '../../form/LoadingButton';
import SubmitButton from '../../form/SubmitButton';
import { toInputDateTimeLocal } from '../../../utils/toInputDateTimeLocal';

export interface CoupounFormData {
  code: string;
  discountPercentage: number;
  maxUsage: number;
  startDate: string | null;
  expiryDate: string | null;
}

interface UpdateCoupounFormProps {
  id: number;
  defaultValues: CoupounFormData;
  onSubmitSuccess: () => void;
}

function UpdateCoupounForm({ id, defaultValues, onSubmitSuccess }: UpdateCoupounFormProps) {
  const { t } = useTranslation();

  const { register, handleSubmit, watch, formState: { errors } } = useForm<CoupounFormData>({
    defaultValues: {
      code: defaultValues.code,
      discountPercentage: defaultValues.discountPercentage,
      maxUsage: defaultValues.maxUsage,
      startDate:toInputDateTimeLocal(defaultValues.startDate??"") ?? "",
      expiryDate: toInputDateTimeLocal(defaultValues.expiryDate??"") ?? ""
    }
  });

  const start = watch("startDate");
  const { loading, updateResource } = useUpdateResource();

  const onSubmit = async (data: CoupounFormData) => {
    let dataToSend: any = {
      code: data.code,
      discountPercentage: +data.discountPercentage,
      maxUsage: +data.maxUsage,
    };

    if (data.startDate) {
      dataToSend.startDate = new Date( data.startDate).toISOString();
    }
    if (data.expiryDate) {
      dataToSend.expiryDate = new Date( data.expiryDate).toISOString();
    }

    await updateResource(
      `coupon/${id}`,
      { ...dataToSend },
      "admin",
      {
        method: "PATCH",
        successMessage: t("updatedSuccess"),
        invalidateQueries:["coupons-by-admin"],
      }
    );

    onSubmitSuccess();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        {/* Code */}
        <InputField
          placeholder=""
          label={t("code")}
          register={register("code", { required: t("required_field") as string })}
          type="text"
          error={errors.code}
        />

        {/* Discount Percentage */}
        <InputField
          placeholder=""
          label={t("discountPercentage")}
          register={register("discountPercentage", {
            required: t("required_field") as string,
            valueAsNumber: true,
            min: { value: 1, message: t("discount_min") },
            max: { value: 100, message: t("discount_max") }
          })}
          type="number"
          error={errors.discountPercentage}
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {/* Max Usage */}
        <InputField
          placeholder=""
          label={t("maxUsage")}
          register={register("maxUsage", {
            required: t("required_field") as string,
            valueAsNumber: true,
            min: { value: 1, message: t("maxUsage_min") }
          })}
          type="number"
          error={errors.maxUsage}
        />

        {/* Start Date (optional) */}
        <InputField
          placeholder=""
          label={t("startDate")}
          register={register("startDate")}
          type="datetime-local"
          error={errors.startDate}
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {/* Expiry Date (optional, must be after startDate if provided) */}
        <InputField
          placeholder=""
          label={t("expiryDate")}
          register={register("expiryDate", {
            validate: (value) =>
              !value ||
              !start ||
              new Date(value) > new Date(start) ||
              t("expiryDate_must_be_after_startDate")
          })}
          type="datetime-local"
          error={errors.expiryDate}
        />
      </div>

      {loading ? <LoadingButton /> : <SubmitButton label={t("update")} />}
    </form>
  );
}

export default UpdateCoupounForm;
